import { ImageUploader } from "@/components/ImageUploader";
import type { FurnitureItem, StorageLocation } from "@/types/api";
import { FURNITURE_STATUS } from "@/types/api";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { inventoryApi } from "./api";
import { categoriesKeys, furnitureKeys } from "./queryKeys";
import { ReservationHistoryDialog } from "./ReservationHistoryDialog";
import { StorageLocationDialog } from "./StorageLocationDialog";

interface ItemDraft extends Partial<FurnitureItem> {
  _new?: boolean;
}

interface Form {
  name: string;
  category: number | null;
  width: number | null;
  height: number | null;
  length: number | null;
  notes: string;
  image: number | null;
  imageUrl: string | null;
  tags: string[];
  items: ItemDraft[];
}

const empty: Form = {
  name: "",
  category: null,
  width: null,
  height: null,
  length: null,
  notes: "",
  image: null,
  imageUrl: null,
  tags: [],
  items: [],
};

function roomLabel(room: ItemDraft["roomInfo"]): string {
  if (!room) return "—";
  const addr = room.stagingInfo?.address ?? "";
  const name = room.name ?? "";
  return addr ? `${addr} (${name})` : name || "—";
}

function itemLocation(it: ItemDraft): string {
  if (it.roomInfo) return roomLabel(it.roomInfo);
  if (it.locationInfo) {
    const cat = it.locationInfo.categoryInfo?.name ?? "";
    const parts = [cat, it.locationInfo.bin, it.locationInfo.slot].filter(Boolean);
    return parts.join(" ") || "—";
  }
  return "—";
}

function storageLocationLabel(it: ItemDraft): string {
  if (it.locationInfo) {
    const cat = it.locationInfo.categoryInfo?.name ?? "";
    const parts = [cat, it.locationInfo.bin, it.locationInfo.slot].filter(Boolean);
    return parts.join(" ") || "—";
  }
  return "—";
}

export function InventoryEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const itemId = isNew ? null : Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(empty);
  const [tagInput, setTagInput] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [locationTarget, setLocationTarget] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const detail = useQuery({
    queryKey: furnitureKeys.detail(itemId),
    queryFn: () => inventoryApi.getFurniture(itemId as number),
    enabled: itemId !== null,
  });

  const categories = useQuery({
    queryKey: categoriesKeys.all,
    queryFn: inventoryApi.listCategories,
  });

  useEffect(() => {
    if (!detail.data) return;
    const f = detail.data;
    setForm({
      name: f.name,
      category: f.category,
      width: f.width,
      height: f.height,
      length: f.length,
      notes: f.notes,
      image: f.image,
      imageUrl: f.imageFile?.relativePath ?? null,
      tags: (f.tags ?? []).map((t) => t.name),
      items: f.items ?? [],
    });
  }, [detail.data]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name,
        category: form.category,
        width: form.width,
        height: form.height,
        length: form.length,
        notes: form.notes,
        image: form.image,
        tags: form.tags,
        items: form.items.map((it) => ({
          id: it._new || !it.id || it.id < 0 ? null : it.id,
          status: it.status ?? 0,
          purchaseDate: it.purchaseDate ?? null,
          purchaseCost: it.purchaseCost ?? null,
          storageLocation: it.storageLocation ?? null,
          notes: it.notes ?? "",
        })),
      };

      return isNew
        ? await inventoryApi.createFurniture(body)
        : await inventoryApi.updateFurniture(itemId as number, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: furnitureKeys.lists() });
      void qc.invalidateQueries({ queryKey: furnitureKeys.detail(itemId) });
      void nav("/administration/inventory");
    },
    onError: (e: Error) => setErr(e.message),
  });

  function addTag(v: string) {
    const t = v.trim().toLowerCase();
    if (!t || form.tags.includes(t)) return;
    setForm({ ...form, tags: [...form.tags, t] });
    setTagInput("");
  }

  function removeItem(idx: number) {
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  }

  function updateItem(idx: number, patch: Partial<ItemDraft>) {
    const next = [...form.items];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, items: next });
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 140 }}>
          <Typography variant="h6">{isNew ? "Inventory" : "Inventory"}</Typography>
        </Box>
        {!isNew && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setHistoryOpen(true)}
            sx={{ whiteSpace: "nowrap" }}
          >
            <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
              VIEW RESERVATION HISTORY
            </Box>
            <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>HISTORY</Box>
          </Button>
        )}
        <Button
          size="small"
          color="error"
          variant="contained"
          onClick={() => void nav("/administration/inventory")}
        >
          CANCEL
        </Button>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          SAVE
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, sm: 2, md: 3 } }}>
        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left: image */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: "center" }}>
              <ImageUploader
                url={form.imageUrl}
                onUploaded={(file) => setForm({ ...form, image: file.id, imageUrl: file.url })}
                onCleared={() => setForm({ ...form, image: null, imageUrl: null })}
                height={300}
              />
            </Paper>
          </Grid>

          {/* Right: fields */}
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              required
              label="Name"
              margin="dense"
              variant="standard"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              fullWidth
              select
              required
              label="Category"
              margin="dense"
              variant="standard"
              value={form.category ?? ""}
              onChange={(e) =>
                setForm({ ...form, category: e.target.value ? Number(e.target.value) : null })}
            >
              <MenuItem value="">—</MenuItem>
              {(categories.data ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                type="number"
                label="Width"
                margin="dense"
                variant="standard"
                value={form.width ?? ""}
                onChange={(e) =>
                  setForm({ ...form, width: e.target.value ? Number(e.target.value) : null })}
              />
              <TextField
                type="number"
                label="Height"
                margin="dense"
                variant="standard"
                value={form.height ?? ""}
                onChange={(e) =>
                  setForm({ ...form, height: e.target.value ? Number(e.target.value) : null })}
              />
              <TextField
                type="number"
                label="Depth"
                margin="dense"
                variant="standard"
                value={form.length ?? ""}
                onChange={(e) =>
                  setForm({ ...form, length: e.target.value ? Number(e.target.value) : null })}
              />
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              margin="dense"
              variant="standard"
              placeholder="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 300) })}
              helperText={`${form.notes.length} / 300`}
            />

            {/* Tags */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tags
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.5, alignItems: "center" }}
              >
                {form.tags.map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    onDelete={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })}
                  />
                ))}
                <TextField
                  variant="standard"
                  placeholder="Enter a tag"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addTag(tagInput);
                    }
                  }}
                  onBlur={() => addTag(tagInput)}
                  sx={{ minWidth: 120 }}
                  inputProps={{ size: 14 }}
                />
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* Units table */}
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 2 }} />
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: { xs: 1100, lg: 0 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>ID</TableCell>
                  <TableCell sx={{ width: 200 }}>STATUS</TableCell>
                  <TableCell>CURRENT LOCATION</TableCell>
                  <TableCell>RESERVED LOCATION</TableCell>
                  <TableCell>STORAGE LOCATION</TableCell>
                  <TableCell align="center" sx={{ width: 70 }}>
                    AVAIL.
                  </TableCell>
                  <TableCell sx={{ width: 180 }}>PURCHASE DATE</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>
                    COST
                  </TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.items.map((it, idx) => {
                  const stagingIsActive = it.roomInfo?.stagingInfo?.status === 0
                    || it.roomInfo?.stagingInfo?.status === 1;
                  const isAvailable = (it.status === 0 || it.status === 1)
                    && (!it.room || !stagingIsActive);
                  return (
                    <TableRow key={it.id ?? `new-${idx}`}>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {it._new || !it.id || it.id < 0 ? "—" : `#${it.id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          variant="standard"
                          fullWidth
                          value={it.status ?? 0}
                          onChange={(e) => updateItem(idx, { status: Number(e.target.value) })}
                        >
                          {Object.entries(FURNITURE_STATUS).map(([k, v]) => (
                            <MenuItem key={k} value={k}>
                              {v}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell
                        onClick={() =>
                          it.roomInfo
                          && void nav(
                            `/staging/${it.roomInfo.staging}/room-builder/${it.roomInfo.id}`,
                          )}
                        sx={{
                          cursor: it.roomInfo ? "pointer" : "default",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={it.roomInfo
                            ? {
                              color: "primary.main",
                              fontWeight: 500,
                              "&:hover": { textDecoration: "underline" },
                            }
                            : undefined}
                        >
                          {itemLocation(it)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        onClick={() =>
                          it.reservationInfo
                          && void nav(
                            `/staging/${it.reservationInfo.staging}/room-builder/${it.reservationInfo.id}`,
                          )}
                        sx={{
                          cursor: it.reservationInfo ? "pointer" : "default",
                          "&:hover": it.reservationInfo ? { bgcolor: "action.hover" } : undefined,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={it.reservationInfo
                            ? {
                              color: "primary.main",
                              fontWeight: 500,
                              "&:hover": { textDecoration: "underline" },
                            }
                            : undefined}
                        >
                          {roomLabel(it.reservationInfo)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        onClick={() => setLocationTarget(idx)}
                        sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                      >
                        <Typography variant="body2">{storageLocationLabel(it)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isAvailable ? null : <CancelIcon color="error" fontSize="small" />}
                      </TableCell>
                      <TableCell>
                        <DatePicker
                          value={it.purchaseDate ? new Date(it.purchaseDate) : null}
                          onChange={(d) =>
                            updateItem(idx, { purchaseDate: d ? d.toISOString() : null })}
                          slotProps={{ textField: { size: "small", variant: "standard" } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          variant="standard"
                          type="number"
                          value={it.purchaseCost ?? ""}
                          onChange={(e) =>
                            updateItem(idx, {
                              purchaseCost: e.target.value ? Number(e.target.value) : null,
                            })}
                          inputProps={{ style: { textAlign: "right" } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton color="error" size="small" onClick={() => removeItem(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            size="small"
            sx={{ mt: 1, color: "primary.main" }}
            onClick={() =>
              setForm({
                ...form,
                items: [
                  ...form.items,
                  {
                    id: -Date.now(),
                    furniture: itemId as number,
                    status: 0,
                    room: null,
                    roomReservation: null,
                    storageLocation: null,
                    purchaseDate: null,
                    purchaseCost: null,
                    notes: "",
                    _new: true,
                  },
                ],
              })}
          >
            + ADD ONE MORE
          </Button>
        </Box>
      </Box>

      <ReservationHistoryDialog
        open={historyOpen}
        furnitureId={itemId}
        furnitureName={form.name}
        onClose={() => setHistoryOpen(false)}
      />

      <StorageLocationDialog
        open={locationTarget !== null}
        onClose={() => setLocationTarget(null)}
        initial={locationTarget !== null
          ? (form.items[locationTarget]?.locationInfo ?? null)
          : null}
        onSave={(location: StorageLocation) => {
          if (locationTarget !== null) {
            updateItem(locationTarget, { storageLocation: location.id, locationInfo: location });
          }
          setLocationTarget(null);
        }}
      />
    </Box>
  );
}
